import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Smartphone, Share2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useHideEmergentBadge } from "@/hooks/useHideEmergentBadge";

const REFRESH_MS = 5000;

export default function WalletPassPage() {
  const { token } = useParams();
  const [pass, setPass] = useState(null);
  const [err, setErr] = useState(null);

  useHideEmergentBadge();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/wallet-pass/${token}`);
      setPass(data);
      setErr(null);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Override the app's PWA manifest with a per-pass manifest so "Add to Home Screen"
  // launches THIS pass URL, not the app root. Also set iOS-specific meta so the
  // home-screen icon opens fullscreen, no Safari chrome.
  useEffect(() => {
    if (!token) return;
    const head = document.head;
    const prev = head.querySelector('link[rel="manifest"]');
    if (prev) prev.dataset.original = prev.href;

    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = `${process.env.REACT_APP_BACKEND_URL}/api/public/wallet-pass/${token}/manifest.json`;
    link.id = "pass-manifest";
    head.appendChild(link);

    // iOS treats <meta name="apple-mobile-web-app-capable"> + the current URL
    // as the home-screen launch target. Ensure it's enabled here.
    const iosCapable = document.createElement("meta");
    iosCapable.name = "apple-mobile-web-app-capable";
    iosCapable.content = "yes";
    iosCapable.id = "pass-ios-capable";
    head.appendChild(iosCapable);

    return () => {
      head.querySelector("#pass-manifest")?.remove();
      head.querySelector("#pass-ios-capable")?.remove();
      if (prev) head.appendChild(prev);
    };
  }, [token]);

  // Refresh when the tab is brought back to the foreground.
  useEffect(() => {
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: pass?.business?.name, url }); }
      catch { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
      catch { toast.error("Copy failed"); }
    }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9fb] p-6">
        <div className="text-center max-w-sm">
          <div className="text-xs uppercase tracking-[0.24em] text-[#a01010] font-semibold mb-2">Pass not found</div>
          <p className="text-[#5a5a65]">{err}</p>
        </div>
      </div>
    );
  }
  if (!pass) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9fb]">
        <div className="text-sm uppercase tracking-[0.2em] text-[#002fa7] font-semibold">Loading…</div>
      </div>
    );
  }

  const brand = pass.business.brand_color || "#002FA7";
  const customerName = pass.customer.name || pass.customer.phone;
  const nextReward = pass.rewards?.find((r) => r.points_cost > pass.points);
  const progress = nextReward ? Math.min(100, Math.round((pass.points / nextReward.points_cost) * 100)) : null;

  return (
    <div
      className="min-h-screen flex flex-col items-center p-4 sm:p-6"
      style={{
        background: `radial-gradient(circle at 50% 0%, ${brand}33 0%, #f9f9fb 60%)`,
      }}
      data-testid="wallet-pass-page"
    >
      <div className="w-full max-w-md mt-4 mb-3 flex items-center justify-between text-[11px] uppercase tracking-[0.24em] font-semibold text-[#5a5a65]">
        <span className="flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" style={{ color: brand }} />
          SparkLoop
        </span>
        <button
          data-testid="wallet-share-btn"
          onClick={share}
          className="flex items-center gap-1.5 hover:text-[#0a0a0a]"
        >
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
      </div>

      {/* Pass card */}
      <div
        className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: brand, color: "white" }}
      >
        <div className="px-6 pt-7 pb-4">
          <div className="text-[10px] uppercase tracking-[0.28em] opacity-70 font-semibold mb-1">
            Loyalty card
          </div>
          <div className="font-display text-3xl font-extrabold tracking-tight leading-tight">
            {pass.business.name}
          </div>
        </div>

        <div className="mx-6 mb-5 rounded-2xl bg-white text-[#0a0a0a] p-5 text-center">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#5a5a65] font-semibold mb-1">
            Your balance
          </div>
          <div
            data-testid="wallet-points"
            className="font-display text-6xl font-extrabold tracking-tighter"
            style={{ color: brand }}
          >
            {pass.points.toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#5a5a65] font-semibold">pts</div>
          {nextReward && (
            <div className="mt-4">
              <div className="text-[11px] text-[#5a5a65] mb-1.5 flex items-center justify-between">
                <span>Next: {nextReward.title}</span>
                <span className="font-mono-data">{pass.points}/{nextReward.points_cost}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#f0f0f4] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${progress}%`, backgroundColor: brand }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="bg-white px-6 py-5 text-center text-[#0a0a0a]">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#5a5a65] font-semibold mb-3">
            Show this code at checkout
          </div>
          <div className="inline-block bg-white p-3 rounded-2xl">
            <QRCodeSVG
              value={pass.customer.phone}
              size={180}
              level="M"
              bgColor="#ffffff"
              fgColor="#0a0a0a"
              data-testid="wallet-qr"
            />
          </div>
          <div className="mt-3 font-mono-data text-sm text-[#5a5a65]">
            {customerName}
          </div>
        </div>
      </div>

      {/* Lifetime stats */}
      <div className="w-full max-w-md grid grid-cols-2 gap-3 mt-4">
        <Stat label="Visits" value={pass.visits} />
        <Stat label="Lifetime pts" value={pass.lifetime_points} />
      </div>

      {/* Save to home screen hint */}
      <div className="w-full max-w-md mt-5 bg-white border border-[#e5e5eb] rounded-2xl p-4 flex items-start gap-3 text-sm">
        <Smartphone className="w-5 h-5 text-[#002fa7] flex-shrink-0 mt-0.5" />
        <div className="text-[#0a0a0a]">
          <div className="font-semibold mb-1">Save it to your phone</div>
          <div className="text-[#5a5a65] text-xs leading-relaxed">
            <span className="font-semibold text-[#0a0a0a]">iPhone:</span> tap{" "}
            <span className="font-mono-data">Share → Add to Home Screen</span>.{" "}
            <span className="font-semibold text-[#0a0a0a]">Android:</span> tap{" "}
            <span className="font-mono-data">⋮ → Install app</span>.
          </div>
        </div>
      </div>

      <div className="mt-6 mb-4 text-[10px] uppercase tracking-[0.24em] text-[#5a5a65] font-semibold">
        Powered by SparkLoop
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[#e5e5eb] rounded-2xl p-3 text-center">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#5a5a65] font-semibold mb-1">
        {label}
      </div>
      <div className="font-display text-2xl font-extrabold">{(value || 0).toLocaleString()}</div>
    </div>
  );
}
